import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Download,
  FileText,
  HelpCircle,
  History,
  Pencil,
  Printer,
  Send,
  Trash2,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { config } from "@/shared/lib/config";
import {
  useCompanyPolicyDetailQuery,
  useDeleteCompanyPolicyMutation,
  useUpdateCompanyPolicyStatusMutation,
} from "../api/queries";
import {
  PolicyDetailTour,
  usePolicyDetailTour,
} from "../tour/PolicyDetailTour";
import { PolicyVersionsList } from "../ui/PolicyVersionsList";

function formatDate(s: string) {
  try {
    return new Date(s).toLocaleDateString("lo-LA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return s;
  }
}

/** S3/MinIO key → API URL (same as contracts / leave attachment). */
function getPolicyAttachmentUrl(
  fileKey: string | null | undefined,
): string | null {
  if (!fileKey || typeof fileKey !== "string" || fileKey.trim() === "")
    return null;
  return `${config.apiUrl}/files/${fileKey}`;
}

function policyDownloadFilename(fileKey: string, title: string): string {
  const fromKey = fileKey.split("/").pop()?.trim();
  if (fromKey && /\.[a-zA-Z0-9]{2,8}$/.test(fromKey)) return fromKey;
  const safeTitle =
    title
      .replace(/[^\p{L}\p{N}\s-]/gu, "")
      .trim()
      .slice(0, 80) || "policy";
  return `${safeTitle.replace(/\s+/g, "_")}.pdf`;
}

function statusLabel(status: string): string {
  switch (status) {
    case "draft":
      return "ຮ່າງ";
    case "active":
      return "ໃຊ້ງານ";
    case "archived":
      return "ເກັບໄວ້";
    default:
      return status;
  }
}

export function PolicyDetailPage() {
  const params = useParams({ strict: false });
  const id = params?.id as string | undefined;
  const nav = useNavigate();
  const detailQuery = useCompanyPolicyDetailQuery(id);
  const policy = detailQuery.data;
  const perms = usePermissions();
  const canUpdate = perms.has(Permissions.hr_company_policy.update);
  const canPublish =
    perms.has(Permissions.hr_company_policy.publish) || canUpdate;
  const canDelete = perms.has(Permissions.hr_company_policy.delete);
  const statusMutation = useUpdateCompanyPolicyStatusMutation();
  const deleteMutation = useDeleteCompanyPolicyMutation();
  const tour = usePolicyDetailTour();

  const attachmentUrl = policy ? getPolicyAttachmentUrl(policy.fileKey) : null;

  const handleViewAttachment = () => {
    if (!attachmentUrl) return;
    window.open(attachmentUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadAttachment = () => {
    if (!policy?.fileKey || !attachmentUrl) return;
    const link = document.createElement("a");
    link.href = attachmentUrl;
    link.download = policyDownloadFilename(policy.fileKey, policy.title);
    link.click();
  };

  const handlePrintContent = () => {
    window.print();
  };

  const handlePublish = () => {
    if (!id) return;
    statusMutation.mutate(
      { id, dto: { status: "active" } },
      {
        onSuccess: () => {
          toast.success("ເຜີຍແພ່ນະໂຍບາຍແລ້ວ");
          detailQuery.refetch();
        },
        onError: () => toast.error("ບໍ່ສາມາດເຜີຍແພ່ໄດ້"),
      },
    );
  };

  const handleBack = () =>
    nav({
      to: "/app/hr/company-policy",
      search: {
        offset: 0,
        limit: 20,
        policyTypeId: undefined,
        status: undefined,
        search: undefined,
        sort: undefined,
      },
    });
  const handleEdit = () =>
    nav({
      to: "/app/hr/company-policy/$id/edit",
      params: { id: id ?? "" },
    });
  const handleVersions = () =>
    nav({
      to: "/app/hr/company-policy/$id/versions",
      params: { id: id ?? "" },
    });

  const handleDelete = () => {
    if (!id || !policy || policy.status !== "draft") return;
    if (!window.confirm("ລຶບນະໂຍບາຍນີ້? ການກະທຳນີ້ບໍ່ສາມາດກູ້ຄືນໄດ້.")) return;
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast.success("ລຶບນະໂຍບາຍແລ້ວ");
        handleBack();
      },
      onError: () => toast.error("ບໍ່ສາມາດລຶບໄດ້"),
    });
  };

  if (detailQuery.isLoading || !policy) {
    return (
      <>
        <Header />
        <Main>
          <div className="text-muted-foreground">
            {detailQuery.isLoading ? "ກຳລັງໂຫຼດ..." : "ບໍ່ພົບນະໂຍບາຍ"}
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <PolicyDetailTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2 print:hidden"
          data-tourid="policy-detail-toolbar"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">{policy.title}</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">{policy.code}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
            {attachmentUrl && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleViewAttachment}
                  className="gap-2"
                  title="ເປີດເອກະສານໃນແທັບໃໝ່"
                >
                  <FileText className="h-4 w-4" />
                  ເບິ່ງເອກະສານ
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadAttachment}
                  className="gap-2"
                  title="ດາວໂຫຼດໄຟລ໌ທີ່ແນບ"
                >
                  <Download className="h-4 w-4" />
                  ດາວໂຫຼດ
                </Button>
              </>
            )}
            {(canUpdate || canPublish || canDelete) && (
              <>
                {canUpdate && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleEdit}
                    className="gap-2"
                  >
                    <Pencil className="h-4 w-4" />
                    ແກ້ໄຂ
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrintContent}
                  className="gap-2 print:hidden"
                  title="ພິມ ຫຼື ບັນທຶກເປັນ PDF ຈາກບຣາວເຊີ"
                >
                  <Printer className="h-4 w-4" />
                  ພິມ
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleVersions}
                  className="gap-2"
                >
                  <History className="h-4 w-4" />
                  ປະຫວັດສະບັບ
                </Button>
                {policy.status === "draft" && canDelete && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                    ລຶບ
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mx-auto max-w-3xl space-y-6">
          <Card data-tourid="policy-detail-info">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-xl">{policy.title}</CardTitle>
                  {policy.code && (
                    <p className="text-muted-foreground text-sm">
                      {policy.code}
                    </p>
                  )}
                </div>
                <span className="rounded-md bg-muted px-2 py-1 text-sm">
                  {statusLabel(policy.status)}
                </span>
              </div>
              {policy.category && (
                <p className="text-muted-foreground text-sm">
                  ປະເພດ: {policy.category}
                </p>
              )}
              <p className="text-muted-foreground text-sm">
                ວັນທີມີຜົນບັງຄັບໃຊ້: {formatDate(policy.effectiveDate)}
              </p>
            </CardHeader>
            {policy.description && (
              <CardContent className="pt-0">
                <p className="text-muted-foreground text-sm">
                  {policy.description}
                </p>
              </CardContent>
            )}
          </Card>

          <Card data-tourid="policy-detail-content">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">ເນື້ອໃນ</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {policy.content ? (
                <div
                  id="policy-detail-print-root"
                  className="prose prose-sm dark:prose-invert max-w-none rounded-md border bg-muted/30 p-4 [&_ol]:list-decimal [&_ul]:list-disc"
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: Policy content is trusted HTML from TipTap editor
                  dangerouslySetInnerHTML={{
                    __html: policy.content,
                  }}
                />
              ) : policy.fileKey ? (
                <div className="space-y-4 rounded-md border bg-muted/30 p-4">
                  <p className="text-muted-foreground text-sm">
                    ນະໂຍບາຍນີ້ເກັບເປັນເອກະສານແນບ (
                    <span className="break-all font-mono text-xs">
                      {policy.fileKey.split("/").pop() ?? policy.fileKey}
                    </span>
                    ).
                  </p>
                  <div className="flex flex-wrap gap-2 print:hidden">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleViewAttachment}
                      className="gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      ເບິ່ງ
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleDownloadAttachment}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      ດາວໂຫຼດ
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">ບໍ່ມີເນື້ອໃນ</p>
              )}
            </CardContent>
          </Card>

          <div className="print:hidden">
            <PolicyVersionsList policyId={policy.id} />
          </div>
          <div
            className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] backdrop-blur supports-backdrop-filter:bg-background/80 print:hidden"
            data-tourid="policy-detail-actions"
          >
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              ຍົກເລີກ
            </Button>
            {policy.status === "draft" && canPublish && (
              <Button
                variant="default"
                size="sm"
                onClick={handlePublish}
                className="gap-2"
                disabled={statusMutation.isPending}
              >
                <Send className="h-4 w-4" />
                ເຜີຍແພ່
              </Button>
            )}
          </div>
        </div>
      </Main>
    </>
  );
}
